import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { FILE_NAME_TYPE } from '../../commons/constants/constants';
import {
  createDraftFloorplan,
  deleteDraftFloorplan,
  updateDraftFloorplanMarker,
  updateDraftFloorplans,
} from '../../services/draftFloorplan.service';
import { createFloorplan } from '../../services/floorplan.service';
import { uploadFiles } from '../../services/uploader.service';
import { MarkerData } from '../../types/MarkerData';

import { RootModel } from '../models';
import { storeName as tourDetailStore } from './configTour/tourDetail.model';
import { storeName as draftTourStore } from './draftTour.model';

export const storeName = 'draftFloorplans';

const initialState = {
  visible: false,
  loading: false,
};

export const draftFloorplans: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof draftFloorplans>) => ({
    async saveMarker(payload, state) {
      const { markers } = payload;
      const { id, code } = state[tourDetailStore].data;

      try {
        await createFloorplan({
          tour: { id },
          floorplan: {
            ...payload,
            markers: markers.map((i: MarkerData) => ({
              x: i.x,
              y: i.y,
              toScene: { id: i.toScene!.id },
            })),
          },
        });

        message.success('Success to update floorplan');
        dispatch[tourDetailStore].getTour(code);
      } catch (err) {
        message.error(`Fail to update floorplan : ${err.message}`);
        console.error(err.message);
        throw err;
      }
    },
    async uploadDraftFloorplanImage(payload, state) {
      this.setLoading(true);
      const { images: imageFiles, tourId } = payload;
      const { user } = state;

      try {
        const uploadResp = await uploadFiles(
          null,
          imageFiles,
          FILE_NAME_TYPE.IMAGE,
          user.id,
          'floorplans'
        );

        const { filesList } = uploadResp;
        if (!filesList.length || !tourId) return;

        const { id, name, url } = filesList[0];

        const { data } = await createDraftFloorplan(tourId, {
          tour: { id: tourId },
          floorplan: { ...payload, image: { id, name, url } },
        });

        if (data && data.image && data.image.url) {
          // preload floorplan image
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              resolve();
            };
            img.onerror = () => {
              resolve();
            };
            img.src = data.image.url;
          });
        }

        message.success('Success to upload floorplan image');
        return data;
      } catch (err) {
        message.error(`Fail to upload floorplan image : ${err.message}`);
        console.error(err.message);
        throw err;
      } finally {
        this.setLoading(false);
      }
    },
    async deleteDraftFloorplan(payload) {
      this.setLoading(true);
      const { tourId, floorplanId } = payload;
      try {
        await deleteDraftFloorplan(tourId, floorplanId);
        message.success('Success to delete floorplan');
        dispatch[draftTourStore].getDraftTour(tourId);
      } catch (err) {
        message.error(`Fail to delete floorplan : ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async saveDraftFloorPlans(payload) {
      try {
        const { tourId, floorplans: plans = [] } = payload;
        const plansHaveImg = plans.filter((p) => !!p.image);
        await updateDraftFloorplans(tourId, {
          tour: { id: tourId },
          floorplans: plansHaveImg.map((floorplan) => {
            return {
              ...floorplan,
              markers: floorplan.markers.map((i: MarkerData) => ({
                ...i,
                code: undefined,
              })),
            };
          }),
        });

        message.success('Success to update floorplan');
        dispatch[draftTourStore].getDraftTour(tourId);
      } catch (err) {
        message.error(`Fail to update floorplan : ${err.message}`);
        console.error(err.message);
        throw err;
      }
    },
    async updateDraftFloorplanMarker(payload) {
      this.setLoading(true);
      const { tourId, floorplanId, ...rest } = payload;
      try {
        const { data } = await updateDraftFloorplanMarker(tourId, floorplanId, rest);
        message.success(`Success to update marker ${rest.name}`);
        dispatch[draftTourStore].getDraftTour(tourId);
        return data;
      } catch (err) {
        message.error(`Fail to update marker ${rest.name}: ${err.message}`);
        console.error(err.message);
        return null;
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default draftFloorplans;
